import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import useInit from "@/lib/queries/user/useInit";
import { Link } from "react-router-dom";
import { SETTINGS_SUBSCRIPTION } from "@/lib/links";
import { useGenerateButtonTexts } from "@/lib/constants/app";
import { useMemo } from "react";
import useUser from "@/lib/queries/user/useUser";
import CheckoutAlert from "../CheckoutAlert";
import { MagicWand } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

function GenerateButton({
  isLoading,
  isDisabled,
  authStatus,
  isSuccess,
  creditsRemaining,
  creditsMax,
}: {
  isLoading: boolean;
  isSuccess: boolean;
  isDisabled: boolean;
  authStatus: {
    isLoggedIn: boolean;
    subscribed: boolean;
    hasCredits: boolean;
  };
  creditsRemaining?: number;
  creditsMax?: number;
}) {
  const { t } = useTranslation("common");
  const { data } = useInit();
  const { data: user } = useUser({
    enabled: authStatus.isLoggedIn,
  });
  const generateButtonTexts = useGenerateButtonTexts();

  const GENERATE_TEXT = useMemo(() => {
    return generateButtonTexts[
      Math.floor(Math.random() * generateButtonTexts.length)
    ];
  }, []); // Empty dependency array to only run once on mount
  let usesCredits = false;
  switch (window.location.pathname) {
    case "/lesson-lab":
      usesCredits =
        data?.tools?.find((f) => f.name === "Lesson Lab")?.usesCredits ?? true;
      break;
    case "/communications-generator":
      usesCredits =
        data?.tools?.find((f) => f.name === "Communications Generator")
          ?.usesCredits ?? true;
      break;
    case "/resource-customizer":
      usesCredits =
        data?.tools?.find((f) => f.name === "Resource Customizer")
          ?.usesCredits ?? true;
      break;
    case "/researcher":
      usesCredits =
        data?.tools?.find((f) => f.name === "Researcher")?.usesCredits ?? true;
      break;
    case "/catchup-coach":
      usesCredits =
        data?.tools?.find((f) => f.name === "Catchup Coach")?.usesCredits ??
        true;
      break;
    default:
      usesCredits = false;
      break;
  }

  // Not logged in
  if (!authStatus.isLoggedIn) {
    const requiresAuthToSubmit = [
      "/lesson-lab",
      "/resource-customiser",
      "/catchup-coach",
    ].includes(window.location.pathname);

    if (requiresAuthToSubmit) {
      const returnTo = window.location.pathname;
      return (
        <Link to={`/authenticate?returnTo=${encodeURIComponent(returnTo)}`}>
          <Button
            id="tour-generate"
            type="button"
            disabled={isDisabled}
            isLoading={isLoading}
            variant="default"
            size={"lg"}
            className={cn(
              "bg-meter-optimum rounded-full px-4 py-3 !text-xl text-white"
            )}
          >
            <MagicWand className="mr-2 whitespace-normal" />
            {t("generate.signInToGenerate")}
          </Button>
        </Link>
      );
    }

    return (
      <Button
        id="tour-generate"
        type="submit"
        disabled={isDisabled}
        isLoading={isLoading}
        variant="default"
        size={"lg"}
        className={cn(
          "bg-meter-optimum rounded-full px-4 py-3 !text-xl text-white"
        )}
      >
        <MagicWand className="mr-2 whitespace-normal" />
        {GENERATE_TEXT}
      </Button>
    );
  }

  // Logged in but no credits
  if (!authStatus.hasCredits) {
    return <CheckoutAlert />;
  }

  // Logged in with credits
  // Calculate visibility conditions for credits display
  const usedMoreThanHalf =
    creditsMax !== undefined &&
    creditsMax > 0 &&
    creditsRemaining !== undefined &&
    creditsRemaining < creditsMax / 2;
  const criticallyLow =
    creditsRemaining !== undefined && creditsRemaining < 5;

  return (
    <div className="flex flex-col items-center w-full gap-2">
      {/* Generate Button */}
      <Button
        id="tour-generate"
        type="submit"
        disabled={isDisabled}
        variant={isSuccess ? "outline" : "default"}
        isLoading={isLoading}
        className="rounded-full !px-6 !py-4 h-fit text-md @sm:text-base"
      >
        <MagicWand className="w-6 h-6 mr-2 whitespace-normal" />
        {isSuccess ? t("generate.regenerate") : GENERATE_TEXT}
      </Button>

      {/* Credits display - only show if user has used more than half */}
      {usesCredits &&
        !authStatus.subscribed &&
        usedMoreThanHalf &&
        user &&
        !user.paused && (
          <div className="flex items-center justify-center gap-2 mt-1 text-sm">
            <span
              className={cn(
                criticallyLow ? "text-red-600" : "text-muted-foreground"
              )}
            >
              {t("generate.toolUsesRemaining", {
                remaining: creditsRemaining,
                max: creditsMax,
              })}
            </span>
            {criticallyLow && <CheckoutAlert inline />}
          </div>
        )}

      {/* Subscription paused warning */}
      {usesCredits && authStatus.subscribed && user?.paused && (
        <p className="px-2 py-1 mt-1 text-sm rounded text-warning-foreground bg-warning border-warning-foreground">
          {t("generate.subscriptionPaused")}{" "}
          <Link to={SETTINGS_SUBSCRIPTION} className="text-blue-500 underline">
            {t("generate.resumeSubscription")}
          </Link>{" "}
          {t("generate.resumeSubscriptionSuffix")}
        </p>
      )}
    </div>
  );
}

export default GenerateButton;
